import axios from 'axios';
import { encryptData, decryptData } from '../components/FormComponents/IDCardFields';
import { createNote, uploadImageAsNote, getNoteAttachment } from './noteService';

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";
const MODULE_NAME = "id_idcards";

// Fields exposed in the ID Card Details UI and available in the SuiteCRM module
const IDCARD_MODULE_FIELDS = [
  "id", // Required for edit operations
  "idtype",
  "name", // This will be mapped to idcardname in UI
  "idnumber",
  "issuedate",
  "expirydate",
  "parentid",
  "date_entered",
  "date_modified",
  "deleted",
  "description",
  "image1",
  "image2",
];

// Map UI field aliases to SuiteCRM fields where names differ
const UI_FIELD_ALIASES = {
  idcardname: "name",
  idcarddescription: "description",
};

// Reverse mapping for reading from API
const REVERSE_UI_FIELD_ALIASES = {
  name: "idcardname",
  description: "idcarddescription",
};

// Fields that need encryption/decryption
const ENCRYPTED_FIELDS = ["idnumber", "issuedate", "expirydate"];

const postSetEntry = async (restData) => {
  const body = new URLSearchParams({
    method: "set_entry",
    input_type: INPUT_TYPE,
    response_type: RESPONSE_TYPE,
    rest_data: JSON.stringify(restData),
  });

  return axios.post(API_URL, body.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

/**
 * Filter outgoing payload to include only module fields used in the UI
 * and encrypt sensitive fields
 */
const filterIDCardPayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
      // allow empty strings for explicit clearing only when field explicitly present
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
    }

    // Check if field needs alias mapping
    const mappedField = UI_FIELD_ALIASES[key] || key;

    if (IDCARD_MODULE_FIELDS.includes(mappedField)) {
      const isInlineBase64Image =
        (mappedField === "image1" || mappedField === "image2") &&
        typeof normalizedValue === "string" &&
        normalizedValue.startsWith("data:image");

      if (isInlineBase64Image) {
        return;
      }

      // Encrypt sensitive fields before sending to API
      if (ENCRYPTED_FIELDS.includes(mappedField) && normalizedValue) {
        payload[mappedField] = encryptData(normalizedValue.toString());
      } else {
        payload[mappedField] = normalizedValue;
      }
    }
  });

  console.log('📦 Filtered ID card payload:', payload);
  return payload;
};

/**
 * Process ID card data entries and decrypt sensitive fields
 */
const processIDCardData = async (entryList, sessionId) => {
  console.log('🔧 processIDCardData called with sessionId:', sessionId ? 'PRESENT' : 'MISSING');
  console.log('🔧 Number of entries to process:', entryList?.length || 0);
  
  if (!Array.isArray(entryList)) return [];

  const processedEntries = entryList.map((entry) => {
    const values = Object.fromEntries(
      Object.values(entry.name_value_list || {}).map((field) => [field.name, field.value])
    );

    const filteredValues = {};
    IDCARD_MODULE_FIELDS.forEach((field) => {
      if (values[field] !== undefined && values[field] !== null) {
        // Decrypt sensitive fields when reading from API
        if (ENCRYPTED_FIELDS.includes(field) && values[field]) {
          filteredValues[field] = decryptData(values[field]);
        } else {
          filteredValues[field] = values[field];
        }
      }
    });

    // Apply reverse alias mapping for UI convenience
    Object.entries(REVERSE_UI_FIELD_ALIASES).forEach(([moduleField, uiField]) => {
      if (filteredValues[moduleField] !== undefined) {
        filteredValues[uiField] = filteredValues[moduleField];
      }
    });

    return {
      id: entry.id,
      ...filteredValues,
    };
  });

  // Retrieve images from notes if sessionId is provided
  if (sessionId) {
    console.log('🔍 Starting image retrieval for', processedEntries.length, 'entries');
    for (const entry of processedEntries) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📋 Processing ID card entry:', entry.id);
      console.log('   image1 RAW value:', JSON.stringify(entry.image1));
      console.log('   image1 type:', typeof entry.image1);
      console.log('   image1 length:', entry.image1 ? entry.image1.length : 0);
      console.log('   image2 RAW value:', JSON.stringify(entry.image2));
      console.log('   image2 type:', typeof entry.image2);
      console.log('   image2 length:', entry.image2 ? entry.image2.length : 0);
      
      if (entry.image1) {
        // Check if it's already base64 data or a note ID
        if (entry.image1.startsWith('data:')) {
          console.log('⚠️  image1 is already base64 data, skipping note fetch');
        } else {
          try {
            console.log('📥 Attempting to fetch image1 from note ID:', entry.image1);
            const imageData = await getNoteAttachment(sessionId, entry.image1);
            if (imageData) {
              console.log('✅ Image1 data retrieved successfully, length:', imageData.length);
              entry.image1 = imageData;
            } else {
              console.log('❌ No image1 data returned from getNoteAttachment');
            }
          } catch (error) {
            console.error('❌ Error fetching image1 note attachment:', error);
          }
        }
      } else {
        console.log('ℹ️  No image1 value present');
      }
      
      if (entry.image2) {
        // Check if it's already base64 data or a note ID
        if (entry.image2.startsWith('data:')) {
          console.log('⚠️  image2 is already base64 data, skipping note fetch');
        } else {
          try {
            console.log('📥 Attempting to fetch image2 from note ID:', entry.image2);
            const imageData = await getNoteAttachment(sessionId, entry.image2);
            if (imageData) {
              console.log('✅ Image2 data retrieved successfully, length:', imageData.length);
              entry.image2 = imageData;
            } else {
              console.log('❌ No image2 data returned from getNoteAttachment');
            }
          } catch (error) {
            console.error('❌ Error fetching image2 note attachment:', error);
          }
        }
      } else {
        console.log('ℹ️  No image2 value present');
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  }

  console.log('Final processed entries with images:', processedEntries);
  return processedEntries;
};

/**
 * Upload ID card image and return note ID
 * @param {string} sessionId - Session ID
 * @param {string} idCardId - ID Card module record ID
 * @param {string} base64Image - Base64 encoded image
 * @param {string} imageType - Type of image ('front' or 'back')
 * @returns {Promise<string>} - Note ID
 */
export const uploadIDCardImage = async (sessionId, idCardId, base64Image, imageType = 'front') => {
  try {
    if (!base64Image || !idCardId) {
      return null;
    }

    // Create a note first
    const noteName = `ID Card ${imageType === 'front' ? 'Front' : 'Back'} Page`;
    const noteId = await createNote(sessionId, MODULE_NAME, idCardId, noteName);

    // Upload the image to the note
    const filename = `idcard_${imageType}_${Date.now()}.jpg`;
    await uploadImageAsNote(sessionId, noteId, base64Image, filename, MODULE_NAME, idCardId);

    return noteId;
  } catch (error) {
    console.error(`❌ Error uploading ID card ${imageType} image:`, error);
    throw error;
  }
};

/**
 * Fetch all ID card data from idcard module
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of ID card objects
 */
export const fetchIDCardData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      select_fields: IDCARD_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return await processIDCardData(entryList, sessionId);
  } catch (error) {
    console.error('Error fetching ID card data:', error);
    throw new Error('Failed to fetch ID card data');
  }
};

/**
 * Fetch ID card entries for a specific parent/user
 * @param {string} sessionId
 * @param {string} parentId
 * @returns {Promise<Array>} - Array of ID card entries associated with the parent
 */
export const fetchIDCardDetailsByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) return [];

  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      query: `${MODULE_NAME}.parentid = '${parentId}' AND ${MODULE_NAME}.deleted = 0`,
      order_by: "date_entered",
      offset: 0,
      select_fields: IDCARD_MODULE_FIELDS,
      max_results: 200,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return await processIDCardData(entryList, sessionId);
  } catch (error) {
    console.error('Error fetching ID card data by parent:', error);
    return [];
  }
};

/**
 * Add new ID card entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {Object} data - ID card data object
 * @returns {Promise<Object>} - Created entry data
 */
export const addIDCardData = async (sessionId, data) => {
  try {
    // First create the ID card entry to get its ID
    const response = await postSetEntry({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filterIDCardPayload(data)).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });
    const createdId = response.data.id;

    // Upload images as note attachments if they exist
    const updatedData = { ...data };
    
    if (data.image1 && data.image1.startsWith('data:')) {
      console.log('📤 Uploading front image...');
      const noteId = await uploadIDCardImage(sessionId, createdId, data.image1, 'front');
      console.log('✅ Front image uploaded, note ID:', noteId);
      updatedData.image1 = noteId;
    }
    
    if (data.image2 && data.image2.startsWith('data:')) {
      console.log('📤 Uploading back image...');
      const noteId = await uploadIDCardImage(sessionId, createdId, data.image2, 'back');
      console.log('✅ Back image uploaded, note ID:', noteId);
      updatedData.image2 = noteId;
    }

    // If images were uploaded, update the record with note IDs
    if ((data.image1 && data.image1.startsWith('data:')) || (data.image2 && data.image2.startsWith('data:'))) {
      const updateData = { ...updatedData, id: createdId };
      console.log('🔄 Updating ID card with note IDs:', { image1: updateData.image1, image2: updateData.image2 });
      
      const updateResponse = await postSetEntry({
        session: sessionId,
        module_name: MODULE_NAME,
        name_value_list: Object.entries(filterIDCardPayload(updateData)).map(([key, value]) => ({
          name: key,
          value: value
        }))
      });
      console.log('✅ ID card updated with note IDs. Response:', updateResponse.data);
    }

    return response.data;
  } catch (error) {
    console.error('Error adding ID card data:', error);
    throw new Error('Failed to add ID card data');
  }
};

/**
 * Edit existing ID card entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to update
 * @param {Object} data - Updated ID card data object
 * @returns {Promise<Object>} - Updated entry data
 */
export const editIDCardData = async (sessionId, id, data) => {
  try {
    // Include the id in the data object before filtering
    const dataWithId = { ...data, id };
    
    // Upload new images as note attachments if they are base64 data
    const updatedData = { ...dataWithId };
    
    if (data.image1 && data.image1.startsWith('data:')) {
      const noteId = await uploadIDCardImage(sessionId, id, data.image1, 'front');
      updatedData.image1 = noteId;
    }
    
    if (data.image2 && data.image2.startsWith('data:')) {
      const noteId = await uploadIDCardImage(sessionId, id, data.image2, 'back');
      updatedData.image2 = noteId;
    }
    
    const response = await postSetEntry({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filterIDCardPayload(updatedData)).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });
    return response.data;
  } catch (error) {
    console.error('Error editing ID card data:', error);
    throw new Error('Failed to edit ID card data');
  }
};

/**
 * Delete ID card entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteIDCardData = async (sessionId, id) => {
  try {
    const response = await postSetEntry({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: [
        { name: "id", value: id },
        { name: "deleted", value: 1 },
      ],
    });

    if (response?.data?.id !== id) {
      console.warn(`Unexpected delete response for ID card entry ${id}:`, response?.data);
    }

    return response.data;
  } catch (error) {
    console.error('Error deleting ID card data:', error);
    throw new Error('Failed to delete ID card data');
  }
};

/**
 * Synchronize ID card entries for a user with SuiteCRM
 * @param {string} sessionId
 * @param {string} parentId
 * @param {Array} entries - ID card entries prepared from UI data
 * @param {Array} removedIds - IDs of entries to remove
 */
export const syncIDCardEntries = async (sessionId, parentId, entries = [], removedIds = []) => {
  if (!sessionId || !parentId) return;

  const sanitizedEntries = Array.isArray(entries) ? entries : [];
  const removalSet = new Set((removedIds || []).filter(Boolean));

  const existingEntries = await fetchIDCardDetailsByParent(sessionId, parentId);
  const existingById = new Map(existingEntries.map((entry) => [entry.id, entry]));
  const existingByIdNumber = new Map(
    existingEntries
      .filter((entry) => entry.idnumber)
      .map((entry) => [entry.idnumber.toLowerCase(), entry])
  );

  const usedIds = new Set();

  for (const sourceEntry of sanitizedEntries) {
    if (!sourceEntry || typeof sourceEntry !== "object") continue;

    const prepared = {
      ...sourceEntry,
      parentid: parentId,
    };

    const filteredPayload = filterIDCardPayload(prepared);

    const payloadKeys = Object.keys(filteredPayload);
    const hasData = payloadKeys.some(
      (key) => key !== "parentid" && filteredPayload[key] !== undefined && filteredPayload[key] !== null && filteredPayload[key] !== ""
    );
    if (!hasData) continue;

    const nameValueList = Object.entries(filteredPayload).map(([key, value]) => ({
      name: key,
      value: value,
    }));

    let targetId = sourceEntry.id || undefined;

    if (!targetId && sourceEntry.idnumber) {
      const match = existingByIdNumber.get(sourceEntry.idnumber.toLowerCase());
      if (match) {
        targetId = match.id;
      }
    }

    if (targetId && existingById.has(targetId)) {
      nameValueList.push({ name: "id", value: targetId });
    }

    const response = await postSetEntry({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: nameValueList,
    });
    const savedId = response?.data?.id || targetId;

    if (savedId) {
      usedIds.add(savedId);
    }
  }

  // Soft delete entries that were removed in the UI
  const entriesToDelete = existingEntries.filter((entry) => {
    if (!entry.id) return false;
    if (removalSet.has(entry.id)) return true;
    return !usedIds.has(entry.id);
  });

  for (const entry of entriesToDelete) {
    try {
      await deleteIDCardData(sessionId, entry.id);
    } catch (error) {
      console.error(`Failed to delete ID card entry ${entry.id}:`, error);
    }
  }
};
